import { fetchWithAuth } from "../fetchWithAuth";

// SSE-Client fuer cProxy POST /chat/stream (Vertrag: ../SL_cProxy/API.md "POST /chat/stream"):
// Zeilen "data: <OpenAI-Chunk>", Usage-Chunk mit choices: [] genau einmal vor "data: [DONE]",
// Fehler als "data: {"error": ...}" (nach Stream-Beginn nie als HTTP-Status).

export interface StreamUsage {
    prompt_tokens: number;
    completion_tokens: number;
}

export interface StreamChatResult {
    text: string;
    usage: StreamUsage | null;
    // true = regulaeres [DONE] empfangen; false = Verbindung vorher abgerissen (Antwort unvollstaendig)
    completed: boolean;
    aborted: boolean;
}

// Stream konnte gar nicht erst aufgebaut werden (HTTP-Fehler vor Stream-Beginn / Netzwerk) -
// der Aufrufer darf dann auf den nicht-streamenden /chat-Pfad zurueckfallen.
export class StreamSetupError extends Error {
    constructor(message: string, public readonly status?: number) {
        super(message);
        this.name = "StreamSetupError";
    }
}

// Fehler-Event mitten im Stream - Teilantwort ist unbrauchbar, KEIN Fallback (Anfrage lief schon).
export class StreamEventError extends Error {
    constructor(message: string, public readonly partialText: string) {
        super(message);
        this.name = "StreamEventError";
    }
}

const isAbortError = (err: unknown) => err instanceof DOMException && err.name === "AbortError";

export const streamChatCompletion = async (
    url: string,
    body: unknown,
    options: { signal?: AbortSignal; onDelta?: (fullText: string) => void } = {},
): Promise<StreamChatResult> => {
    let response: Response;
    try {
        response = await fetchWithAuth(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
            body: JSON.stringify(body),
            signal: options.signal,
        });
    } catch (err) {
        if (isAbortError(err)) return { text: "", usage: null, completed: false, aborted: true };
        throw new StreamSetupError(err instanceof Error ? err.message : String(err));
    }

    if (!response.ok || !response.body) {
        const detail = await response.text().catch(() => "");
        throw new StreamSetupError(detail || `HTTP ${response.status}`, response.status);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let text = "";
    let usage: StreamUsage | null = null;

    const handleLine = (line: string): boolean => {
        if (!line.startsWith("data:")) return false;
        const payload = line.slice(5).trim();
        if (!payload) return false;
        if (payload === "[DONE]") return true;

        let chunk: any;
        try {
            chunk = JSON.parse(payload);
        } catch {
            return false; // unvollstaendige/unbekannte Zeile ignorieren
        }
        if (chunk?.error) {
            throw new StreamEventError(typeof chunk.error === "string" ? chunk.error : JSON.stringify(chunk.error), text);
        }
        // Usage-Chunk hat choices: [] - nie blind choices[0] lesen.
        const delta = Array.isArray(chunk?.choices) ? chunk.choices[0]?.delta?.content : undefined;
        if (typeof delta === "string" && delta.length > 0) {
            text += delta;
            options.onDelta?.(text);
        }
        if (chunk?.usage) {
            usage = {
                prompt_tokens: Number(chunk.usage.prompt_tokens) || 0,
                completion_tokens: Number(chunk.usage.completion_tokens) || 0,
            };
        }
        return false;
    };

    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let newline: number;
            while ((newline = buffer.indexOf("\n")) >= 0) {
                const line = buffer.slice(0, newline).replace(/\r$/, "");
                buffer = buffer.slice(newline + 1);
                if (handleLine(line)) return { text, usage, completed: true, aborted: false };
            }
        }
        // Rest ohne abschliessendes Newline
        if (buffer && handleLine(buffer.replace(/\r$/, ""))) return { text, usage, completed: true, aborted: false };
        return { text, usage, completed: false, aborted: false };
    } catch (err) {
        if (isAbortError(err)) return { text, usage, completed: false, aborted: true };
        throw err;
    } finally {
        reader.releaseLock();
    }
};

// Nur fuer den Ausnahmefall ohne Usage-Chunk (Abbruch durch den Nutzer, Verbindungsabriss): grobe
// Token-Schaetzung (~4 Zeichen/Token), damit der Run nicht mit 0 abgerechnet wird (Masterplan 4b).
// Der Normalfall rechnet ausschliesslich mit den echten usage-Zahlen von cProxy ab.
export const estimateTokensFromChars = (chars: number): number => Math.ceil(chars / 4);
